import { useState, useEffect } from "react";

interface Props {
  member: any;
  onClose: () => void;
  onSave: (data: any) => void;
}

const roleLabels: Record<string, string> = {
  admin: "מנהל מערכת",
  coordinator: "רכז",
};

export default function StaffEditDrawer({ member, onClose, onSave }: Props) {
  const [form, setForm] = useState({
    name: member.name,
    email: member.email,
    role: member.role,
    center: member.center || "",
    staff_status: member.staff_status || member.status || "active",
  });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);

  useEffect(() => {
    setForm({
      name: member.name,
      email: member.email,
      role: member.role,
      center: member.center || "",
      staff_status: member.staff_status || member.status || "active",
    });
    setNewPassword("");
    setConfirmPassword("");
    setPwError("");
    setPwSuccess(false);
  }, [member]);

  const handleSavePassword = () => {
    setPwError("");
    setPwSuccess(false);
    if (!newPassword || !confirmPassword) {
      setPwError("יש למלא את שני השדות");
      return;
    }
    if (newPassword.length < 6) {
      setPwError("הסיסמה חייבת להכיל לפחות 6 תווים");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("הסיסמאות אינן תואמות");
      return;
    }
    setPwSuccess(true);
    setTimeout(() => setPwSuccess(false), 3000);
  };

  const handleSubmit = () => {
    const payload = {
      name: form.name,
      email: form.email,
      role: form.role,
      center: form.center,
      staff_status: form.staff_status,
      ...(newPassword ? { password: newPassword } : {}),
    };
    onSave(payload);
  };

  const inputClass =
    "w-full px-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 bg-rose-50/30 focus:outline-none focus:border-rose-300 transition-all";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div
        className="fixed top-0 left-0 h-full w-full max-w-md bg-white border-r-2 border-rose-100 z-50 overflow-y-auto"
        dir="rtl"
        style={{ fontFamily: "'Heebo', sans-serif" }}
      >
        <div className="sticky top-0 bg-white border-b-2 border-rose-100 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 font-bold text-base flex-shrink-0">
              {member.name.charAt(0)}
            </div>
            <div>
              <p className="font-bold text-gray-800 text-sm">{member.name}</p>
              <span className="text-xs text-gray-400">
                {roleLabels[member.role]}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer transition-all"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-4 space-y-4">
            <p className="text-xs font-bold text-gray-500 flex items-center gap-2">
              <i className="ri-edit-line text-rose-400"></i> עריכת פרטים
            </p>
            <div>
              <label className={labelClass}>שם מלא</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>אימייל</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>תפקיד</label>
              <select
                value={form.role}
                onChange={(e) =>
                  setForm((f) => ({ ...f, role: e.target.value }))
                }
                className={inputClass}
              >
                <option value="admin">מנהל מערכת</option>
                <option value="coordinator">רכז</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>מרכז / אזור</label>
              <input
                type="text"
                value={form.center}
                onChange={(e) =>
                  setForm((f) => ({ ...f, center: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>סטטוס</label>
              <div className="flex items-center gap-2">
                {["active", "inactive"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, staff_status: s }))}
                    className={`flex-1 py-2 rounded-xl border-2 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${form.staff_status === s ? (s === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-300" : "bg-gray-100 text-gray-500 border-gray-300") : "border-gray-200 bg-white text-gray-400 hover:bg-gray-50"}`}
                  >
                    {s === "active" ? "פעיל" : "לא פעיל"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-4 space-y-3">
            <p className="text-xs font-bold text-gray-500 flex items-center gap-2">
              <i className="ri-lock-password-line text-rose-400"></i> סיסמה
            </p>
            <div>
              <label className={labelClass}>סיסמה חדשה</label>
              <div className="relative">
                <input
                  type={showNewPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPwError("");
                    setPwSuccess(false);
                  }}
                  placeholder="הזן סיסמה חדשה"
                  className={inputClass + " pl-10"}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw((v) => !v)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i
                    className={`${showNewPw ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}
                  ></i>
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass}>אימות סיסמה חדשה</label>
              <div className="relative">
                <input
                  type={showConfirmPw ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPwError("");
                    setPwSuccess(false);
                  }}
                  placeholder="הזן שוב את הסיסמה"
                  className={inputClass + " pl-10"}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPw((v) => !v)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i
                    className={`${showConfirmPw ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}
                  ></i>
                </button>
              </div>
            </div>
            {pwError && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-2 border-red-200 rounded-xl text-red-500 text-xs font-semibold">
                <i className="ri-error-warning-line"></i>
                {pwError}
              </div>
            )}
            {pwSuccess && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border-2 border-emerald-200 rounded-xl text-emerald-600 text-xs font-semibold">
                <i className="ri-checkbox-circle-line"></i> הסיסמה עודכנה בהצלחה
              </div>
            )}
            <button
              onClick={handleSavePassword}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-500 text-sm font-bold hover:bg-rose-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-lock-line text-base"></i> שנה סיסמה
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
            >
              שמור שינויים
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              ביטול
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
